import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BarChart3, Coins, Landmark, PiggyBank, Receipt, Percent, Wallet, ArrowLeftRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { CANALI, fmtEUR, fmtPct, nowPeriod } from "@/lib/format";
import { EmptyState, Hint, Loading, PageHeader, PeriodSelect, SimpleSelect, btnPrimary } from "@/components/app/common";
import { KpiCard } from "@/components/app/KpiCard";
import { ChannelChart, CostDonut, LineMarginChart, ProductMarginList, TrendChart } from "@/components/app/charts";
import { useAuth } from "@/context/AuthContext";
import { InsightsCard } from "@/components/app/Insights";
import { BudgetAlertsPanel } from "@/components/app/BudgetAlerts";
import { StockAlert } from "@/components/app/StockAlert";
import { Onboarding } from "@/components/app/Onboarding";

const Panel = ({ title, subtitle, children, className = "", testId }) => (
  <section data-testid={testId} className={`card-soft p-5 sm:p-7 fade-up ${className}`}>
    <div className="mb-5">
      <h2 className="text-lg font-medium">{title}</h2>
      {subtitle && <p className="text-xs text-[#756B64] mt-1">{subtitle}</p>}
    </div>
    {children}
  </section>
);

const Empty = ({ text }) => <div className="py-10 text-center text-sm text-[#756B64]">{text}</div>;

function KpiGrid({ k, prevLabel }) {
  const c = k.corrente, d = k.delta;
  const saldoSub = c.iva_saldo >= 0 ? "IVA da versare" : "IVA a credito";
  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 sm:gap-5">
        <KpiCard testId="kpi-card-fatturato" label="Fatturato (imponibile)" value={fmtEUR(c.fatturato)} delta={d.fatturato} prevLabel={prevLabel} icon={Coins} tone="sage" />
        <KpiCard testId="kpi-card-costi" label="Costi" value={fmtEUR(c.costi)} delta={d.costi} prevLabel={prevLabel} icon={Receipt} tone="terra" invert delay={60} />
        <KpiCard testId="kpi-card-utile" label="Utile" value={fmtEUR(c.utile)} delta={d.utile} prevLabel={prevLabel} icon={PiggyBank} tone="gold" delay={120} />
        <KpiCard testId="kpi-card-margine" label="Margine %" value={fmtPct(c.margine_pct)} delta={d.margine_pct} deltaSuffix=" pt" prevLabel={prevLabel} icon={Percent} tone="plum" delay={180} />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-5 mt-4 sm:mt-5">
        <KpiCard testId="kpi-card-iva-debito" label="IVA a debito" value={fmtEUR(c.iva_debito)} icon={Landmark} tone="blush" sub={c.iva_rc ? `Vendite + di cui reverse charge ${fmtEUR(c.iva_rc)}` : "Sulle vendite (incassi)"} delay={220} />
        <KpiCard testId="kpi-card-iva-credito" label="IVA a credito" value={fmtEUR(c.iva_credito)} icon={Wallet} tone="blush" sub={c.iva_rc || c.iva_importazione ? `di cui RC ${fmtEUR(c.iva_rc)}${c.iva_importazione ? ` · import ${fmtEUR(c.iva_importazione)}` : ""}` : "Sugli acquisti (costi)"} delay={260} />
        <KpiCard testId="kpi-card-iva-saldo" label="Saldo IVA" value={fmtEUR(c.iva_saldo)} icon={ArrowLeftRight} tone={c.iva_saldo >= 0 ? "terra" : "sage"} sub={saldoSub} delay={300} />
      </div>
    </>
  );
}

function ProductsSection({ prodotti, canale }) {
  if (canale === "Generale") return <Hint testId="products-generale-hint">L'analisi prodotti non è disponibile per il canale "Generale": le vendite prodotto sono registrate solo su E-commerce e Rete fisica.</Hint>;
  if (!prodotti.n_prodotti)
    return <EmptyState testId="products-empty" icon={BarChart3} title="Nessuna vendita prodotto nel periodo" text="Inserisci le quantità vendute in Vendite mensili per vedere i margini per prodotto." action={<Link to="/vendite-mensili" className={`${btnPrimary} inline-flex items-center`}>Vai a Vendite mensili</Link>} />;
  const t = prodotti.totali;
  return (
    <>
      <div className="grid lg:grid-cols-2 gap-5">
        <Panel testId="panel-top-products" title="Top 5 prodotti per margine" subtitle="Margine = (prezzo − costo) × quantità">
          <ProductMarginList items={prodotti.top} tone="#8AA090" testId="top-products" />
        </Panel>
        <Panel testId="panel-bottom-products" title="Ultimi 5 prodotti per margine" subtitle="I prodotti che contribuiscono meno">
          <ProductMarginList items={prodotti.bottom} tone="#D98A72" testId="bottom-products" />
        </Panel>
      </div>
      <div className="grid lg:grid-cols-2 gap-5 mt-5">
        <Panel testId="panel-brands" title="Top marche per margine netto marca" subtitle={`Margine netto marca = lordo − omaggi − scarti − spese brand · ricavo prodotti ${fmtEUR(t.ricavo)} · margine lordo ${fmtEUR(t.margine)} (${fmtPct(t.margine_pct)})${prodotti.spese_brand_generale ? ` · spese brand generali non ripartite ${fmtEUR(prodotti.spese_brand_generale)}` : ""}`}>
          <LineMarginChart data={prodotti.marche} labelKey="marca" marginKey="margine_netto_marca" marginLabel="Margine netto marca" />
          <Link to="/analisi-margini" data-testid="brands-analisi-link" className="mt-3 inline-block text-xs font-semibold text-[#8C6D62] hover:underline">Analisi completa per marca →</Link>
        </Panel>
        <Panel testId="panel-lines" title="Margine per tipologia" subtitle="Costo del venduto al costo medio ponderato">
          <LineMarginChart data={prodotti.linee} />
        </Panel>
      </div>
    </>
  );
}

export default function AdminDashboard() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(nowPeriod());
  const [canale, setCanale] = useState("all");
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = { anno: period.anno, ...(period.mese ? { mese: period.mese } : {}), ...(canale !== "all" ? { canale } : {}) };
    api.get("/dashboard", { params }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [period, canale]);

  return (
    <div data-testid="admin-dashboard">
      <PageHeader overline={`Ciao ${user?.name?.split(" ")[0] || ""}`} title="Dashboard" subtitle={data ? `Andamento di ${data.periodo.label}${canale !== "all" ? ` · ${canale}` : ""}` : " "}>
        <PeriodSelect anno={period.anno} mese={period.mese} onChange={setPeriod} allowYear prefix="dashboard" />
        <SimpleSelect testId="dashboard-channel-select" className="w-40" value={canale} onChange={setCanale} options={[{ value: "all", label: "Tutti i canali" }, ...CANALI.map((c) => ({ value: c, label: c }))]} />
      </PageHeader>
      <div className="mb-5 space-y-3"><BudgetAlertsPanel /><StockAlert /></div>
      {!data ? <Loading /> : <DashboardBody data={data} canale={canale} />}
    </div>
  );
}

function DashboardBody({ data, canale }) {
  const vuoto = !data.has_data && !data.trend.some((t) => t.incassi || t.costi);
  return (
    <div className="space-y-5">
      {vuoto && <Onboarding />}
      {!data.has_data && !vuoto && <Hint testId="dashboard-period-empty">Nessun movimento in {data.periodo.label}. Il grafico dell'andamento mostra comunque gli ultimi 12 mesi.</Hint>}
      <KpiGrid k={data.kpi} prevLabel={data.periodo.prev_label} />
      {!vuoto && <InsightsCard anno={data.periodo.anno} mese={data.periodo.mese || (data.periodo.anno === new Date().getFullYear() ? new Date().getMonth() + 1 : 12)} />}
      <Panel testId="panel-trend" title="Andamento ultimi 12 mesi" subtitle="Incassi, costi e utile (imponibile)">
        {vuoto ? <Empty text="Il grafico apparirà dopo aver registrato i primi incassi e costi in Movimenti." /> : <TrendChart data={data.trend} />}
      </Panel>
      <div className="grid lg:grid-cols-5 gap-5">
        <Panel testId="panel-costs-donut" title="Costi per categoria" className="lg:col-span-3">
          {data.costi_per_categoria.length ? <CostDonut data={data.costi_per_categoria} /> : <Empty text="Nessun costo nel periodo." />}
          {data.merce_non_venduta?.totale > 0 && <p className="text-xs text-[#756B64] mt-4 border-t border-[#EAE3DC] pt-3" data-testid="donut-mnv-note">Informativo (non incluso nei costi): Omaggi {fmtEUR(data.merce_non_venduta.omaggi_valore)} · Merce deteriorata {fmtEUR(data.merce_non_venduta.scarti_valore)}</p>}
        </Panel>
        <Panel testId="panel-channels" title="Confronto canali" subtitle="E-commerce, Rete fisica e costi generali" className="lg:col-span-2">
          <ChannelChart data={data.canali} />
        </Panel>
      </div>
      <div className="pt-6">
        <div className="overline-label mb-2">Analisi prodotti</div>
        <h2 className="text-2xl font-medium mb-3">Margini per prodotto</h2>
        <Hint testId="products-source-hint">Questa sezione usa le <b>Vendite mensili per prodotto</b>, separate dai movimenti: il fatturato in alto deriva invece dagli incassi registrati nei Movimenti. Così non c'è doppio conteggio.</Hint>
        <div className="mt-5"><ProductsSection prodotti={data.prodotti} canale={canale} /></div>
        {data.merce_non_venduta && (
          <section data-testid="merce-non-venduta-card" className="card-soft p-5 sm:p-6 mt-5 flex flex-wrap items-center gap-6">
            <div>
              <div className="overline-label !text-[#756B64]">Valore merce non venduta</div>
              <div className="text-2xl font-semibold tabular mt-1" data-testid="merce-non-venduta-totale">{fmtEUR(data.merce_non_venduta.totale)}</div>
            </div>
            <div className="text-sm"><span className="text-[#756B64]">Omaggi</span> <b className="tabular">{fmtEUR(data.merce_non_venduta.omaggi_valore)}</b> <span className="text-xs text-[#756B64]">({data.merce_non_venduta.omaggi_qty} pz)</span></div>
            <div className="text-sm"><span className="text-[#756B64]">Merce deteriorata</span> <b className="tabular">{fmtEUR(data.merce_non_venduta.scarti_valore)}</b> <span className="text-xs text-[#756B64]">({data.merce_non_venduta.scarti_qty} pz)</span></div>
            {data.merce_non_venduta.per_causale?.length > 0 && (
              <div className="basis-full flex flex-wrap gap-2" data-testid="mnv-per-causale">
                {data.merce_non_venduta.per_causale.map((c, i) => (
                  <span key={c.causale} data-testid={`mnv-causale-${i}`} className="text-xs px-3 py-1.5 rounded-full bg-[#E8C5C8]/50 text-[#7A4E52]"><b>{c.causale}</b> · {fmtEUR(c.valore)} ({c.qty} pz)</span>
                ))}
              </div>
            )}
            <p className="text-xs text-[#756B64] basis-full">Valorizzati al costo medio ponderato. Informativo: non sono sommati ai Costi totali, perché la merce è già conteggiata negli acquisti (Merce/Acquisti). <Link to="/analisi-margini" className="underline text-[#8C6D62]">Vai all'analisi margini</Link></p>
          </section>
        )}
      </div>
    </div>
  );
}
